import React, { useEffect, useRef, useState } from 'react';
import Collapse from 'react-css-collapse';

import { ReactComponent as Checkmark } from '../../../images/checkmark.svg';
import { ReactComponent as Caret } from '../../../images/icons/down-caret.svg';
import { mergeUrlParams } from '../../../tools/textTools';
import ContentfulImage from '../../Common/ContentfulImage';
import { PrimaryLink } from '../../Common/Links';
import RichTextConverter from '../../Common/RichText/RichTextConverter';
import FeatureTable from '../FeatureTable';
import QuoteSidebar from './QuoteSidebar';
import Sidebar from './Sidebar';
import * as styles from './Tab.module.scss';

const TabHeading = ({ tab }) => {
  return (
    <div className={styles.tabHeading}>
      {tab.headingIcon && (
        <ContentfulImage imageFragment={tab.headingIcon} lazy={true} className={styles.headingIcon} />
      )}
      {tab.heading && <h2>{tab.heading}</h2>}
    </div>
  );
};

const ContentColumn = ({ tab, showHeading = true }) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.search) {
      setSearchQuery(window.location.search);
    }
  }, []);

  let url = tab.ctaButton?.url;
  url = mergeUrlParams(url, searchQuery);

  return (
    <div className={styles.mainContent}>
      {showHeading && <TabHeading tab={tab} />}
      <RichTextConverter copy={tab.introCopy} className={styles.introCopy} />
      {url && <PrimaryLink to={url} label={tab.ctaButton.linkText} isButton className={styles.ctaButton} />}

      {tab.featureList && (
        <div className={styles.featureList}>
          {tab.heading === 'Fullstory Workforce' && <h3>Includes</h3>}
          <ul>
            {tab.featureList.map((feature, i) => (
              <li key={i}>
                <span className={styles.checkmark}>
                  <Checkmark />
                </span>
                {feature}
              </li>
            ))}
          </ul>
        </div>
      )}

      {tab.planLevels && (
        <div className={styles.planLevels}>
          <h3>{tab.planLevels.heading}</h3>
          {tab.planLevels.content.map((level, i) => (
            <div className={[styles.level, styles[level.accentColor]].join(' ')} key={i}>
              {level.subtitle && <div className={styles.badge}>{level.subtitle}</div>}
              <h4>{level.title}</h4>
              <RichTextConverter copy={level.body} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Tab({ tab, isActive }) {
  const [tableOpen, setTableOpen] = useState(false);
  const ref = useRef();
  const twoColumnLayout = tab.sidebar?.__typename === 'ContentfulComponentPlansTab';

  const toggleTable = () => {
    if (tableOpen && ref.current) {
      window.scrollTo({
        top: ref.current.offsetTop,
        behavior: 'smooth',
      });
    }

    setTableOpen(!tableOpen);
  };

  return (
    <div
      data-analytics-component={'tabbed-folder-tab'}
      data-analytics-id={tab.heading.toLowerCase().replace(/\s/g, '-')}
      className={[styles.tab, styles[tab.accentColor]].join(' ')}
      style={{ display: isActive ? 'block' : 'none' }}
    >
      {twoColumnLayout && <TabHeading tab={tab} />}

      <div
        ref={ref}
        className={styles.grid}
        style={{ gridTemplateColumns: twoColumnLayout ? '1fr 0.0625rem 1fr' : '1.5fr 1fr' }}
      >
        <ContentColumn tab={tab} showHeading={!twoColumnLayout} />

        {twoColumnLayout && <div className={styles.divider} />}

        {tab.sidebar && twoColumnLayout ? (
          <ContentColumn tab={tab.sidebar} showHeading={false} />
        ) : tab.sidebar.type === 'Testimonials' ? (
          <QuoteSidebar sidebar={tab.sidebar} colorTheme={tab.accentColor} />
        ) : (
          <Sidebar sidebar={tab.sidebar} />
        )}
      </div>

      {tab.featureTable && (
        <>
          <button
            className={[styles.featureTableButton, tableOpen ? styles.open : ''].join(' ')}
            onClick={() => setTableOpen(!tableOpen)}
          >
            {tableOpen ? 'Close' : 'Open'} plan comparison table
            <Caret />
          </button>

          <Collapse isOpen={tableOpen}>
            <FeatureTable
              heading={tab.featureTable.heading}
              copy={tab.featureTable.copy}
              tiers={tab.featureTable.data.columns}
              sections={tab.featureTable.data.sections}
              noPadding={true}
            />

            <button
              className={[styles.featureTableButton, tableOpen ? styles.open : ''].join(' ')}
              onClick={toggleTable}
            >
              Close plan comparison table
              <Caret />
            </button>
          </Collapse>
        </>
      )}
    </div>
  );
}
